import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import PageHeader from '../../components/PageHeader/PageHeader';

import { ReactComponent as HeaderIcon }       from '../../assets/site-assets/invest/icon-1.svg';
import { ReactComponent as TourismIcon }      from '../../assets/site-assets/invest/Tourism.svg';
import { ReactComponent as RealEstateIcon }   from '../../assets/site-assets/invest/Real estate.svg';
import { ReactComponent as ManufacturingIcon} from '../../assets/site-assets/invest/Manufacturing.svg';
import { ReactComponent as LogisticsIcon }    from '../../assets/site-assets/invest/Logistics.svg';
import { ReactComponent as HealthcareIcon }   from '../../assets/site-assets/invest/Healthcare.svg';
import { ReactComponent as AgricultureIcon }  from '../../assets/site-assets/invest/Agriculture.svg';
import { ReactComponent as EducationIcon }    from '../../assets/site-assets/invest/Education.svg';
import { ReactComponent as FreeZoneIcon }      from '../../assets/site-assets/invest/free-zone.svg';
import { ReactComponent as LinkIcon }           from '../../assets/site-assets/invest/link.svg';
import { ReactComponent as IndustrialIcon }    from '../../assets/site-assets/invest/industial-zone.svg';
import { ReactComponent as SustainabilityIcon} from '../../assets/site-assets/invest/Sustainability.svg';
import { ReactComponent as OwnershipIcon }     from '../../assets/site-assets/invest/ownership.svg';
import { ReactComponent as DecorStar }         from '../../assets/site-assets/invest/icon-1.svg';
import { ReactComponent as DecorHourglass }    from '../../assets/site-assets/invest/icon-2.svg';
import { ReactComponent as SetupMask }         from '../../assets/site-assets/invest/setting-up-box-mask.svg';

import BgImage   from '../../assets/site-assets/invest/bg.png';
import Freezone1 from '../../assets/site-assets/invest/freezone-1.png';
import Freezone2 from '../../assets/site-assets/invest/freezone-2.png';
import Freezone3 from '../../assets/site-assets/invest/freezone-3.png';
import Freezone4 from '../../assets/site-assets/invest/freezone-4.png';

import './InvestPage.scss';

const statIcons = [FreeZoneIcon, IndustrialIcon, HeaderIcon, OwnershipIcon];

const sectors = [
  { id: 'healthcare',    Icon: HealthcareIcon     },
  { id: 'logistics',     Icon: LogisticsIcon      },
  { id: 'tourism',       Icon: TourismIcon        },
  { id: 'agriculture',   Icon: AgricultureIcon    },
  { id: 'sustainability',Icon: SustainabilityIcon },
  { id: 'manufacturing', Icon: ManufacturingIcon  },
  { id: 'real-estate',   Icon: RealEstateIcon     },
  { id: 'education',     Icon: EducationIcon      },
];

const freezoneImgs = [Freezone1, Freezone2, Freezone3, Freezone4];


const InvestPage = () => {
  const { t, i18n } = useTranslation();
  const [activeSectorId, setActiveSectorId] = useState('healthcare');

  const activeSector = sectors.find(s => s.id === activeSectorId);
  const activeKey = activeSectorId.replace(/-([a-z])/g, (_, c) => c.toUpperCase());

  return (
    <div className="invest-page">

      <PageHeader
        bgImage={BgImage}
        title={t('invest.pageTitle')}
        subtitle={t('invest.subtitle')}
      />

      {/* ── Sharjah has positioned itself ── */}
      <section className="invest-positioned">
        <div className="invest-container">

          <div className="row align-items-start g-5 mb-5">
            <div className="col-12 col-lg-4">
              <h2 className="invest-positioned__title">
                <span className="invest-positioned__sharjah">{t('invest.positioned.titleHighlight')}</span>
                {' '}{t('invest.positioned.titleMain')}
              </h2>
            </div>
            <div className="col-12 col-lg-8">
              <p className="invest-positioned__body">{t('invest.positioned.body')}</p>
            </div>
          </div>

          <div className="invest-positioned__cards">
            {statIcons.map((Icon, i) => (
              <div key={i} className="invest-positioned__card">
                <Icon className="invest-positioned__icon" aria-hidden="true" />
                <span className="invest-positioned__num">{t(`invest.stats.${i}.num`)}</span>
                <span className="invest-positioned__label">
                  {t(`invest.stats.${i}.label1`)}<br />{t(`invest.stats.${i}.label2`)}
                </span>
              </div>
            ))}
          </div>

        </div>

        {/* ── High-Potential Investment Sectors ── */}
        <section className="invest-sectors">
          <div className="invest-container">
            <div className="row g-5 align-items-start">

              <div className="col-12 col-lg-4">
                <h2 className="invest-sectors__title">
                  {t('invest.sectors.titleLine1')}<br />{t('invest.sectors.titleLine2')}
                </h2>
                <p className="invest-sectors__body">{t('invest.sectors.body')}</p>
              </div>

              <div className="col-12 col-lg-8">
                <div className="invest-sectors__panel">
                  <div className="invest-sectors__list">
                    {sectors.map(({ id, Icon }) => {
                      const key = id.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
                      return (
                        <button
                          key={id}
                          className={`invest-sectors__item${activeSectorId === id ? ' active' : ''}`}
                          onClick={() => setActiveSectorId(id)}
                        >
                          <Icon className="invest-sectors__item-icon" />
                          <span>{t(`invest.sectors.items.${key}.label`)}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="invest-sectors__content">
                    {activeSector && (
                      <>
                        <activeSector.Icon className="invest-sectors__content-icon" aria-hidden="true" />
                        <h3 className="invest-sectors__content-title">{t(`invest.sectors.items.${activeKey}.label`)}</h3>
                        <p className="invest-sectors__content-body">{t(`invest.sectors.items.${activeKey}.body`)}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      </section>

      {/* ── The Right Ecosystem For Your Business ── */}
      <section className="invest-ecosystem">
        <DecorStar      className="invest-ecosystem__decor invest-ecosystem__decor--star"      aria-hidden="true" />
        <DecorHourglass className="invest-ecosystem__decor invest-ecosystem__decor--hourglass" aria-hidden="true" />
        <div className="invest-container">
          <h2 className="invest-ecosystem__title">
            {t('invest.ecosystem.titlePre')}{' '}
            <span>{t('invest.ecosystem.titleHighlight')}</span>
          </h2>
          <p className="invest-ecosystem__subtitle">{t('invest.ecosystem.subtitle')}</p>
          <div className="row g-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className={`col-12 col-sm-6 ${i < 3 ? 'col-lg-4' : 'col-lg-6'}`}>
                <div className="invest-ecosystem__card">
                  <h3 className="invest-ecosystem__card-title">{t(`invest.ecosystem.items.${i}.title`)}</h3>
                  <p className="invest-ecosystem__card-body">{t(`invest.ecosystem.items.${i}.body`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Setting Up A Business in Sharjah ── */}
      <section className="invest-setup">
        <SetupMask className="invest-setup__mask" aria-hidden="true" />
        <div className="invest-container">
          <div className="row align-items-center g-5">
            <div className="col-12 col-lg-5">
              <h2 className="invest-setup__title">
                {t('invest.setup.titlePre')}{' '}
                <span>{t('invest.setup.titleHighlight')}</span>
              </h2>
            </div>
            <div className="col-12 col-lg-7">
              <p className="invest-setup__body">{t('invest.setup.body')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Free Zones ── */}
      <section className="invest-freezones">
        <div className="invest-container">
          <div className="row align-items-start g-5 mb-5">
            <div className="col-12 col-lg-4">
              <h2 className="invest-freezones__title">{t('invest.freezones.title')}</h2>
            </div>
            <div className="col-12 col-lg-8">
              <p className="invest-freezones__body">{t('invest.freezones.body')}</p>
            </div>
          </div>
          <Swiper
            className="invest-freezones__swiper"
            effect="coverflow"
            grabCursor={true}
            centeredSlides={true}
            slidesPerView={3}
            coverflowEffect={{ rotate: 0, stretch: 80, depth: 220, modifier: 1, slideShadows: false }}
            modules={[EffectCoverflow]}
            initialSlide={1}
            loop={true}
            key={i18n.language}
            dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
          >
            {freezoneImgs.map((src, i) => (
              <SwiperSlide key={i} className="invest-freezones__slide">
                <div className="invest-freezones__logo-wrap">
                  <img src={src} alt={t(`invest.freezones.items.${i}`)} className="invest-freezones__logo" />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* ── Useful Links ── */}
      <section className="invest-links">
        <div className="invest-container">
          <div className="invest-links__inner">
            <h2 className="invest-links__title">{t('invest.links.title')}</h2>
            <div className="invest-links__grid">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <a key={i} href={t(`invest.links.hrefs.${i}`)} className="invest-links__link">
                  {t(`invest.links.items.${i}`)}
                  <LinkIcon className="invest-links__icon" aria-hidden="true" style={i18n.language === 'ar' ? { transform: 'scaleX(-1)' } : undefined} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default InvestPage;
